'use client';
import React, { MouseEvent, useEffect } from 'react';
import styles from './styles.module.scss';
import { DeviceType, IProduct } from '@mybestbrands/types';
import { AccordionDetail } from '@/app/ui/components/product/accordionDetail';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { COOKIE_NAMES, RETAILER_IDS } from '@/app/constants';
import Image from 'next/image';
import { useDispatch } from 'react-redux';
import { toggleDetailModalVisible } from '@/app/lib/redux';
import { deleteCookie, getCookie } from 'cookies-next';
import { FbViewContentEvent, ProductDetailEvents } from '@/app/ui/components/tracking/events';
import MultipleImages from '@/app/ui/components/multipleimages/desktop';
import MobileGallery from '@/app/ui/components/multipleimages/mobile';
import ProductPrice from '@/app/ui/components/product/productPrice';
import ClickOutButton from '@/app/ui/components/product/clickOutButton';
import ShippingInfo from '@/app/ui/components/product/shippingInfo';
import { RelatedProducts } from '@/app/ui/components/product/relatedProducts';

import { useTranslation } from 'next-i18next';

interface Props {
  productDetails: IProduct;
  locale: string;
  deviceType: DeviceType;
  relatedProducts: IProduct[];
  similarProducts: IProduct[];
}

export function ProductModal({ productDetails, deviceType, locale, relatedProducts, similarProducts }: Props) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation('common');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams ?? '');
  params.delete('product', productDetails.productId);
  const url = `${pathname}?${params.toString()}`;

  useEffect(() => {
    dispatch(toggleDetailModalVisible(true));
  }, []);

  const handleClose = (e: MouseEvent<HTMLElement>): void => {
    const clickedElement = (e.target as Element).className;
    if (clickedElement.indexOf('_close') > -1) {
      const productLoadedFromServer = getCookie(COOKIE_NAMES.PRODUCT_LOADED_FROM_SERVER);
      dispatch(toggleDetailModalVisible(false));
      if (productLoadedFromServer) {
        deleteCookie(COOKIE_NAMES.PRODUCT_LOADED_FROM_SERVER);
        window.location.href = url;
      } else {
        router.back();
      }
    }
  };

  const showSizes =
    productDetails?.categoryTagNames.filter((el) => el === 'clothing') ||
    productDetails?.categoryTagNames.filter((el) => el === 'shoes');

  return (
    <div
      style={{
        inset: 0,
        zIndex: 3,
        backgroundColor: 'rgba(0,0,0,0.2)',
      }}
      onClick={handleClose}
      className={'position-fixed d-flex justify-content-center align-items-end align-items-md-start _close'}>
      <ProductDetailEvents product={productDetails} />
      <div className={`${styles['container']}`}>
        <div className={'d-flex justify-content-end'}>
          <Image
            width={22}
            height={22}
            className={'pb-2 ps-2 cursor-pointer _close'}
            src="/intstatic/icons/close.svg"
            alt="close"
          />
        </div>
        <div className={'row'}>
          {deviceType === DeviceType.DESKTOP ? (
            <MultipleImages product={productDetails} locale={locale} />
          ) : (
            <MobileGallery product={productDetails} locale={locale} />
          )}
          <div className={'col-12 col-lg-5'}>
            <div className={'text-center text-lg-start'}>
              <div className={'fw-bold text-uppercase brandName'}>{productDetails.brandName}</div>
              <div className={'fst-italic fs-6'}>{productDetails.name}</div>
              <div className={`fw-bold fs-6 ${styles.prices} my-3`}>
                <ProductPrice product={productDetails} />
              </div>
            </div>
            <ClickOutButton product={productDetails} locale={locale} />
            <ShippingInfo product={productDetails} />
            <AccordionDetail locale={locale} product={productDetails} showSizes={showSizes} />
          </div>
          {productDetails?.retailerId === RETAILER_IDS.PEEK_CLOPPENBURG && (
            <div
              className="mt-1"
              style={{ fontSize: '0.8rem' }}
              dangerouslySetInnerHTML={{ __html: t('common:P&C_DISCLAIMER') }}
            />
          )}
          {!!relatedProducts?.length && productDetails?.breadcrumb && (
            <RelatedProducts
              product={productDetails}
              relatedProducts={relatedProducts}
              locale={locale}
              isRelatedProducts={true}
              deviceType={deviceType}
              isOverlay={true}
            />
          )}
          {!!similarProducts?.length && (
            <RelatedProducts
              product={productDetails}
              relatedProducts={similarProducts}
              locale={locale}
              deviceType={deviceType}
              isOverlay={true}
            />
          )}
        </div>
      </div>
      <FbViewContentEvent
        viewContent={{
          content_ids: [productDetails.productId],
          content_type: 'product',
          content_name: productDetails.name,
          content_category: productDetails.categoryTagNames.join('/'),
          currency: 'EUR',
          value: productDetails.price / 100,
        }}
      />
    </div>
  );
}
